import React from 'react';
import {
    Box,
    Button,
    Container,
    Divider,
    Link,
    Typography,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import ScrollToTopButton from "./ScrollToTopButton";

export default function Terms() {
    return (
    <Box >
        <ScrollToTopButton />
        <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                bgcolor: '#223f73' }}>
            <Box
            component="img"
            src="/images/logo.png"
            alt="Logo"
            sx={{
                maxWidth: '160px',
                height: 'auto',
                display: 'block',
            }}
            />
            <Button component={NavLink} to='/' sx={{color: 'white', mr: 2}}>
                Home
            </Button>
        </Box>
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
            Terms of Use
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
            Effective Date: April 1, 2025
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
            Last Revised: April 19, 2025
            </Typography >            
            <Box mt={4}>

            <Typography variant="h6">1. Introduction</Typography>
            <Typography paragraph>
            These Terms of Use (“Terms”) form a binding agreement between PR Innova Inc. (“PR Innova,” “we,” “us,” or “our”)—including all of its brands, software platforms, mobile applications, and websites (collectively, the “Services”)—and any person or entity (“you” or “User”) who accesses or uses the Services.
            </Typography>       

            <Typography variant="h6">2. Definitions</Typography>
            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                    display: "flex",
                    mb: 1,
                    // this ensures the two columns are spaced exactly by their flex values
                    gap: 2
                    }}
                >
                    <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flexBasis: "30%", flexShrink: 0 }}
                    >
                    Term
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                    Meaning
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                    sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2
                    }}
                >
                    <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flexBasis: "30%", flexShrink: 0 }}
                    >
                    Platforms
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                    Web portals, APIs, mobile apps, and SaaS solutions developed or hosted by PR Innova Inc.
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                    sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2
                    }}
                >
                    <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flexBasis: "30%", flexShrink: 0 }}
                    >
                    Content
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                    All text, graphics, software, code, data, audio, video, designs, and other material available through the Services.
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                    sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2
                    }}
                >
                    <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flexBasis: "30%", flexShrink: 0 }}
                    >
                    Client Agreement
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                    A separate, signed services, license, or master agreement between PR Innova Inc. and a paying customer.
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                    sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2
                    }}
                >
                    <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ flexBasis: "30%", flexShrink: 0 }}
                    >
                    Brands
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                    Any trade names, trademarks, or business lines operated by PR Innova Inc.,
                    including ServeTech PR and future brands.
                    </Typography>
                </Box>                                
            </Box>
    
            <Typography variant="h6">3. Acceptance & Eligibility</Typography>
            <Typography paragraph>
            By accessing or using any Service, you confirm you (a) are at least 18 years old (or the age of majority in your jurisdiction), (b) have capacity to agree to these Terms, and (c) will comply with all applicable laws.
            </Typography>
    
            <Typography variant="h6">4. Account Registration & Security</Typography>
            <Typography paragraph>
            If a Service requires an account, you must: (i) provide accurate, current information; (ii) promptly update any changes; (iii) maintain the confidentiality of credentials; and (iv) notify us immediately of any unauthorized use. You are responsible for all activity under your account.
            </Typography>
    
            <Typography variant="h6">5. Acceptable Use</Typography>
            <Typography paragraph>You agree NOT to:</Typography>

            <Typography component="div" variant="body1">
            <Typography
                component="ol"
                sx={{
                listStyleType: 'decimal',
                m: 2,
                '& li::marker': {
                    fontWeight: 'bold',
                },
                }}
            >
                <li>Upload malware, attempt to gain unauthorized access, or disrupt network performance;</li>
                <li>Scrape, reverse-engineer, or decompile any portion of the Services;</li>
                <li>Infringe intellectual-property or privacy rights;</li>
                <li>Use the Services to send unsolicited or unlawful communications;</li>
                <li>Violate any applicable export, sanctions, or data-protection laws.</li>
            </Typography>
            </Typography>
            <Typography paragraph>We may suspend or terminate access for suspected violations.</Typography>

            <Typography variant="h6">6. Services & Client Engagement</Typography>
            <Typography paragraph>
            Custom software, consulting, or hosting projects are governed by a Client Agreement. If a conflict exists between these Terms and a Client Agreement, the Client Agreement prevails for that project.
            </Typography>
    
            <Typography variant="h6">7. Fees & Payment (If Applicable)</Typography>
            <Typography paragraph>Some Services may be fee-based. Unless otherwise stated in a Client Agreement:</Typography>

            <Typography component="div" variant="body1">
            <Typography
                component="ul"
                sx={{
                m: 2,
                '& li::marker': {
                    fontWeight: 'bold',
                },
                }}
            >
                <li>Fees are due in U.S. dollars;</li>
                <li>Late payments may incur the lesser of 1.5 % interest per month or the maximum rate permitted by law;</li>
                <li>You are responsible for taxes (excluding our income taxes).</li>
            </Typography>
            </Typography>
    
            <Typography variant="h6">8. Intellectual Property</Typography>
            <Typography paragraph>
            The Services and Content are owned by PR Innova Inc. or licensors and are protected by Puerto Rico, U.S., and international IP laws. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for their intended purpose. All rights not expressly granted are reserved.
            </Typography>
    
            <Typography variant="h6">9. Third-Party Content & Links</Typography>
            <Typography paragraph>
            Links to external sites are provided for convenience only. We do not endorse and are not responsible for third-party content, products, or services.
            </Typography>
    
            <Typography variant="h6">10. Confidentiality</Typography>
            <Typography paragraph>
            If, in using the Services, either party receives non-public information designated as confidential, the receiving party will protect it with at least the same care it uses for its own confidential information and not disclose it except as required by law.
            </Typography>
    
            <Typography variant="h6">11. Privacy</Typography>
            <Typography paragraph>
            Our{' '}<Link component={NavLink} to='/privacy'>Privacy Policy</Link>{' '} explains how we collect, use, and protect personal data. By using the Services, you consent to those practices.
            </Typography>
            
            <Typography variant="h6">12. Communications Terms</Typography>
            <Typography variant="h6">12.1 Email</Typography>
            <Typography>
            Email communications are subject to our <Link component={NavLink} to='/email-terms'>Email Terms & Conditions</Link>.
            </Typography>
            <Typography variant="h6">12.2 SMS / Mobile Messaging</Typography>
            <Typography paragraph>
            Text messaging is governed by our <Link component={NavLink} to='/sms-terms'>SMS Terms of Use</Link>.
            </Typography>

            <Typography variant="h6">13. Feedback</Typography>
            <Typography paragraph>
            You may submit comments or suggestions. You grant us a perpetual, worldwide, royalty-free right to use such feedback without restriction.
            </Typography>

            <Typography variant="h6">14. Disclaimers</Typography>
            <Typography paragraph>
            THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, PR INNOVA INC. DISCLAIMS ALL WARRANTIES—EXPRESS OR IMPLIED—including merchantability, fitness for a particular purpose, non-infringement, accuracy, or availability.
            </Typography>

            <Typography variant="h6">15. Limitation of Liability</Typography>
            <Typography paragraph>
            TO THE FULLEST EXTENT PERMITTED BY LAW, PR INNOVA INC., ITS OFFICERS, EMPLOYEES, AND AFFILIATES WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, DATA, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL LIABILITY UNDER THESE TERMS WILL NOT EXCEED USD $100 OR THE AMOUNTS PAID BY YOU TO US FOR THE SERVICE IN THE 6 MONTHS PRIOR TO THE EVENT, WHICHEVER IS GREATER.
            </Typography>

            <Typography variant="h6">16. Indemnification</Typography>
            <Typography paragraph>
            You will defend, indemnify, and hold PR Innova Inc. harmless from any claims, damages, or expenses (including reasonable attorneys’ fees) arising from your (a) use of the Services, (b) violation of these Terms, or (c) infringement of any third-party right.
            </Typography>

            
            <Typography variant="h6">17. Suspension & Termination</Typography>
            <Typography paragraph>
            We may suspend or terminate access (with or without notice) if you breach these Terms, misuse the Services, or if required by law. Sections intended to survive (e.g., IP, disclaimers, limitation of liability) will remain in force.
            </Typography>

            <Typography variant="h6">18. Governing Law & Dispute Resolution</Typography>
            <Typography paragraph>
            These Terms are governed by the laws of the Commonwealth of Puerto Rico and applicable U.S. federal laws, without regard to conflict-of-law principles. Any dispute not resolved informally will be submitted to binding arbitration in San Juan, Puerto Rico, under the American Arbitration Association’s Commercial Rules. Either party may seek injunctive relief in court for IP or confidentiality breaches.
            </Typography>

            <Typography variant="h6">19. Changes to Terms</Typography>
            <Typography paragraph>
            We may update these Terms at any time. Material changes will be announced via the Service or email. Continued use after the effective date constitutes acceptance.
            </Typography>

            <Typography variant="h6">20. Contact Us</Typography>
            <Typography paragraph>
                If you have any questions about these Terms, please contact us at:
                <br /><br />
                <strong>PR Innova Inc</strong>
                <br />
                127 Calle Guadalquivir
                <br />
                San Juan PR 00927
                <br />
                <Link href="mailto:support@prinnovainc.com">support@prinnovainc.com</Link>
                <br />
            </Typography>
            </Box>
        </Container>
    </Box>
    )
}
